import FontStylesheet from "components/website/components/FontStylesheet";
import StipplLogo from "components/website/img/stippl-logo.svg";
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { isAndroid, isIOS } from "react-device-detect";
import type { LonLat } from "utils/curveLine";
import { cx, MarkerIcon } from "../shared/markers";
import { OPENING_MAX_KM, type CameraTarget } from "./camera";
import ContactTab from "./ContactTab";
import HighlightsTab from "./HighlightsTab";
import { UiIcon } from "./Icon";
import { strings } from "./i18n";
import { logoPhoneUrl, logoUrl, saveMapUrl } from "./links";
import LiveMap from "./LiveMap";
import type { ScenePlace } from "./MapScene";
import { amsterdamDay, liveContent } from "./offers";
import OffersTab from "./OffersTab";
import Sheet, { type Selection } from "./Sheet";
import { sisterMarker, sisterOf } from "./SisterCard";
import { trackPartnerMap, type PartnerMapAction } from "./track";
import type { PartnerMapContent, PartnerTheme, WeekEvent } from "./types";
import { parseViewHash, writeViewHash, type Tab } from "./viewHash";
import { hostelLook, indexPlaces, placeFill, walkLabel, type MappedPlace } from "./walk";
import WeekTab, { DayStrip } from "./WeekTab";
import { eventsOn, firstTime, tonightOn, weekdayIndex } from "./weekProgram";
import styles from "./PartnerMap.module.scss";

/** Bebas Neue for headings, Montserrat for the page, Rubik for times and distances: the marketing pages load Rubik and Poppins only. */
const FONTS = "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Montserrat:wght@400;500;600;700;800&family=Rubik:wght@500;600;700&display=swap";

/** Within this many km a walk is not worth a line on the map. */
const NO_WALK_KM = 0.05;

const themeVars = ({ primary, primaryHover, tint, cream, accent, lit, secondary, fonts }: PartnerTheme): CSSProperties =>
	({
		"--primary": primary,
		"--primary-hover": primaryHover,
		"--tint": tint,
		"--cream": cream,
		"--accent": accent,
		...(lit ? { "--lit": lit } : {}),
		...(secondary ? { "--secondary": secondary } : {}),
		...(fonts ? { "--f-head": fonts.head, "--f-ui": fonts.ui, ...(fonts.script ? { "--f-script": fonts.script } : {}) } : {}),
	}) as CSSProperties;

/** The title as the header draws it: the `script` words that lead it on a line of their own and the rest, or null when it is drawn plainly (no script, or the title does not start with it). */
export const titleParts = ({ title, script }: PartnerMapContent["header"]): { script: string; rest: string } | null =>
	script && title.startsWith(script) ? { script, rest: title.slice(script.length) } : null;

/**
 * The visitor's date, once the page is on screen. Not read while rendering: the server's day is not the visitor's, and
 * a page that says otherwise on first paint than the HTML it was sent would not hydrate.
 */
function useToday(): Date | null {
	const [today, setToday] = useState<Date | null>(null);
	useEffect(() => setToday(new Date()), []);
	return today;
}

/** The places the events of a day happen at, apart from the base, each once. */
const venuesOf = (events: WeekEvent[], places: Record<string, MappedPlace>): MappedPlace[] =>
	[...new Set(events.filter((e) => e.where !== "hostel").map((e) => e.where))].map((slug) => places[slug]);

/** the list has to scroll this far before the phone's "N below" chip goes (and it does not come back) */
const HINT_SCROLL_PX = 30;

/** Scrolls the list so that `target` is `gap` px under its top, gently unless the visitor asked for less motion. */
function scrollListTo(list: HTMLElement, target: HTMLElement, gap: number) {
	const top = list.scrollTop + target.getBoundingClientRect().top - list.getBoundingClientRect().top - gap;
	list.scrollTo?.({ top, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
}

/**
 * A partner's map page, any partner's: `content` is all it knows. A header with the Highlights tab and, when the
 * partner has them, the tabs of its week program, its offers and its contact; the list under it; the Stippl map with the base and the places
 * of the open tab; a detail sheet; "Save this map". On a phone the map is fixed at the top and the list scrolls on
 * its own, on a wide screen the list is a side panel. Opening something fits the base and it into the part of the
 * map the sheet leaves free; closing flies back.
 */
const PartnerMap = ({ content: given }: { content: PartnerMapContent }) => {
	// An offer past its `until` is gone from the whole page. The day is Amsterdam's, read once per render: the server and the browser then say the same
	// (they differ only when this render and the one that hydrates it fall on either side of Amsterdam's midnight, and React then draws the page again).
	const amsterdamToday = amsterdamDay();
	const content = useMemo(() => liveContent(given, amsterdamToday), [given, amsterdamToday]);
	const { partner, header, theme, weekProgram, offers, contact, base } = content;
	const t = strings(content.lang);
	const places = useMemo(() => indexPlaces(content), [content]);
	const hostel = useMemo<LonLat>(() => [content.base.lng, content.base.lat], [content.base]);
	const today = useToday();
	const [tab, setTab] = useState<Tab>("highlights");
	const [chosenDay, setChosenDay] = useState<number | null>(null);
	const [selected, setSelected] = useState<Selection | null>(null);
	const [hover, setHover] = useState<string | null>(null);
	const [camera, setCamera] = useState<CameraTarget>({ kind: "opening" });
	// the offer a place's strip or the base's sheet sent the visitor to: the offers tab scrolls to its card once it is open
	const [offerGoal, setOfferGoal] = useState<string | null>(null);
	// the phone's "N below" chip is gone once the list has been scrolled
	const [scrolled, setScrolled] = useState(false);
	const panelRef = useRef<HTMLDivElement>(null);
	const listRef = useRef<HTMLDivElement>(null);
	/** the tabs besides Highlights and Week program, which the address hash can name */
	const others = useMemo<Tab[]>(() => [...(offers ? (["offers"] as const) : []), ...(contact ? (["contact"] as const) : [])], [offers, contact]);

	const lightHostel = hostelLook(theme);
	const scriptTitle = titleParts(header);
	const todayIdx = today ? weekdayIndex(today) : 0;
	const day = chosenDay ?? todayIdx;
	const events = useMemo(() => eventsOn(weekProgram, day, today), [weekProgram, day, today]);
	const tonight = today ? tonightOn(weekProgram, todayIdx, today) : [];
	const track = (action: PartnerMapAction, item?: string) => trackPartnerMap(content.slug, action, item);

	// ---- what is open, and what the camera does about it ----
	const openEvent = selected?.kind === "event" ? weekProgram?.events.find((e) => e.id === selected.id) : undefined;
	/** the marker that is open: "base", or a place's slug */
	let active: string | null = null;
	if (selected?.kind === "base") active = "base";
	else if (selected?.kind === "place") active = selected.slug;
	else if (openEvent) active = openEvent.where === "hostel" ? "base" : openEvent.where;

	const open = (next: Selection) => {
		setSelected(next);
		// opening a place counts as using the map: a hostel's "N highlights below" is gone for this visit (In de Waag's comes back when the sheet closes)
		if (lightHostel) setScrolled(true);
		const place =
			next.kind === "place" ? places[next.slug] : next.kind === "event" ? places[weekProgram!.events.find((e) => e.id === next.id)!.where] : undefined;
		setCamera(place ? { kind: "place", base: hostel, place: place.point } : { kind: "fit", points: [hostel], maxScale: 1.8, panel: true });
	};

	/** the view a tab opens on: the highlights around the base, or the base and the day's venues */
	const viewOf = (tabId: Tab, dayEvents = events): CameraTarget => {
		// what is on offer is at the base: in close on it, the photos fanned out over it are left to the Highlights
		if (tabId === "offers") return { kind: "fit", points: [hostel], maxScale: 2.4, panel: false };
		if (tabId !== "week") return { kind: "opening" };
		const venues = venuesOf(dayEvents, places);
		// a trip far out (Teotihuacán, 41 km) never sets the view, as an outlying highlight never does (`OPENING_MAX_KM`): it comes into view when it is picked.
		// A day with only such trips has the view the page opens on.
		const near = venues.filter((p) => p.km <= OPENING_MAX_KM);
		if (venues.length > 0 && near.length === 0) return { kind: "opening" };
		return { kind: "fit", points: [hostel, ...near.map((p) => p.point)], maxScale: 2.2, panel: false };
	};

	const close = () => {
		setSelected(null);
		setCamera(viewOf(tab));
	};

	const changeTab = (next: Tab) => {
		if (next === tab) return;
		setTab(next);
		setSelected(null);
		setCamera(viewOf(next));
		writeViewHash(next, chosenDay);
	};

	const home = () => {
		setSelected(null);
		// a hostel's map comes back to the view it opens on
		setCamera(content.map.openAround ? { kind: "opening" } : { kind: "fit", points: [hostel], maxScale: 2.2, panel: false });
	};

	/** an offer's card, from a place's "After this" or the base's sheet: the offers tab, scrolled to it */
	const goToOffer = (id: string) => {
		setSelected(null);
		setOfferGoal(id);
		if (tab !== "offers") changeTab("offers");
		else setCamera(viewOf("offers"));
	};

	// A marker tapped in the Week program tab opens the event that is on there today, in the Highlights tab the place.
	const openFromMap = (key: string) => {
		if (key === "base") return open({ kind: "base" });
		const event = tab === "week" ? events.find((e) => e.where === key) : undefined;
		return open(event ? { kind: "event", id: event.id } : { kind: "place", slug: key });
	};

	// The address keeps the tab and day, so a reload (iOS Safari reloads a page it has been away from, after a link to
	// another site) opens where the visitor was. Read once on the client, after hydrating: the server's HTML is the same for everyone.
	useEffect(() => {
		const view = parseViewHash(window.location.hash, !!weekProgram, others);
		if (view.tab === "highlights") return;
		setTab(view.tab);
		setChosenDay(view.day);
		const now = new Date();
		setCamera(viewOf(view.tab, eventsOn(weekProgram, view.day ?? weekdayIndex(now), now)));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Escape closes what is open. One listener, put up again on every render so that it closes the current tab's view.
	useEffect(() => {
		if (!selected) return undefined;
		const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	});

	// Another tab or day starts at the top of the list.
	useEffect(() => {
		if (listRef.current) listRef.current.scrollTop = 0;
	}, [tab, day]);

	// An offer that was asked for (a place's "After this", the base's sheet) is scrolled to once its tab is there.
	useEffect(() => {
		const list = listRef.current;
		if (!list || tab !== "offers" || !offerGoal) return;
		const card = list.querySelector<HTMLElement>(`#partnermap-offer-${offerGoal}`);
		setOfferGoal(null);
		if (card) scrollListTo(list, card, 12);
	}, [tab, offerGoal]);

	// ---- the map ----
	const sister = useMemo(() => sisterOf(content), [content]);
	const mapPlaces = useMemo<ScenePlace[]>(() => {
		const shown = tab === "week" ? venuesOf(events, places) : content.highlights.map((h) => places[h.slug]);
		// a pinned venue (the sister restaurant) is on the map in every tab
		const pinned = content.venues.filter((v) => v.pinned && !shown.some((p) => p.slug === v.slug)).map((v) => places[v.slug]);
		return [...shown, ...pinned].map((p) => ({
			id: p.slug,
			kind: p.kind,
			name: p.name,
			grp: p.grp,
			icon: p.icon,
			photo: p.photo,
			active: active === p.slug,
			lat: p.lat,
			lng: p.lng,
			pill: tab === "week" && events.some((e) => e.where === p.slug) ? firstTime(events.find((e) => e.where === p.slug)!) : undefined,
			...(p.logo
				? {
						logo: p.logo,
						plate: sister && sister.place.slug === p.slug ? t.sister : undefined,
						color: sister?.sister.color,
						...(sister && sister.place.slug === p.slug ? sisterMarker(sister.sister) : {}),
					}
				: {}),
		}));
	}, [tab, content.highlights, content.venues, events, places, active, sister, t]);

	// The dotted walk from the base to the place a mouse is over, or else the one that is open.
	const walkTo = hover && hover !== "base" ? places[hover] : active && active !== "base" ? places[active] : undefined;
	const walk = useMemo(
		() =>
			walkTo && walkTo.km >= NO_WALK_KM
				? { to: walkTo.point, label: walkTo.reach ?? walkLabel(walkTo.km, content.rideLabel, content.lang, content.bikeFromKm) }
				: null,
		[walkTo, content.rideLabel, content.lang, content.bikeFromKm]
	);

	/** The phone's "N below" chip over the foot: it scrolls the list to its first section, and is gone for good once the list has been scrolled, it has been pressed or (a hostel's) a place is open */
	const moreChip = (label: string) => (
		<button
			type="button"
			id="partnermap-more"
			className={cx(styles.more, scrolled && styles.gone)}
			onClick={() => {
				setScrolled(true);
				const list = listRef.current;
				const first = list?.querySelector<HTMLElement>(`.${styles.sec}, .${styles.offer}`);
				if (list && first) scrollListTo(list, first, 10);
			}}
		>
			<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
				<path d="M6 9l6 6 6-6" />
			</svg>
			<span>{label}</span>
		</button>
	);

	const tabs: { id: Tab; label: string; icon: ReactNode }[] = [
		{ id: "highlights", label: t.tabs.highlights, icon: <UiIcon name="tabMap" /> },
		...(offers ? [{ id: "offers" as const, label: t.tabs.offers, icon: <MarkerIcon name="p_wine" size={16} /> }] : []),
		...(weekProgram ? [{ id: "week" as const, label: t.tabs.week, icon: <UiIcon name="tabWeek" /> }] : []),
		...(contact ? [{ id: "contact" as const, label: t.tabs.contact, icon: <UiIcon name="phone" /> }] : []),
	];

	return (
		<div
			className={cx(
				styles.root,
				theme.lit && styles.dark,
				theme.look === "venue" && styles.venue,
				theme.lightHeader && styles.lightHead,
				theme.wordmarkLogo && styles.wordmarkLogo,
				lightHostel && styles.hostel
			)}
			id="stippl-partner-map"
			style={themeVars(theme)}
			{...(content.lang ? { lang: content.lang } : {})}
		>
			<FontStylesheet href={theme.fonts?.href ?? FONTS} />
			<aside className={styles.rail}>
				<div className={styles.railTop}>
					<div className={styles.brandrow}>
						{header.wordmark ? (
							<img className={styles.wordmark} src={header.wordmark} alt={partner.name} />
						) : (
							<span className={styles.lf}>
								<img src={partner.logo} alt="" />
							</span>
						)}
						<div className={styles.title}>
							<small>{base.kind ?? partner.name}</small>
							<h1>
								{scriptTitle ? (
									<>
										<span className={styles.script}>{scriptTitle.script}</span>
										{scriptTitle.rest}
									</>
								) : (
									header.title
								)}
							</h1>
						</div>
					</div>
					<p className={styles.subtitle}>{header.subtitle}</p>
					<div className={styles.tabs} role="tablist">
						{tabs.map(({ id, label, icon }) => (
							<button
								key={id}
								type="button"
								role="tab"
								id={`partnermap-tab-${id}`}
								className={styles.tab}
								aria-selected={tab === id}
								onClick={() => changeTab(id)}
							>
								{icon}
								{label}
							</button>
						))}
					</div>
				</div>
				{tab === "week" && today && (
					<div className={styles.listhead}>
						<DayStrip
							lang={content.lang}
							today={today}
							todayIdx={todayIdx}
							day={day}
							onDay={(next) => {
								setChosenDay(next);
								setSelected(null);
								writeViewHash("week", next);
							}}
						/>
					</div>
				)}
				<div
					className={styles.list}
					ref={listRef}
					onScroll={(e) => {
						if (e.currentTarget.scrollTop > HINT_SCROLL_PX) setScrolled(true);
					}}
				>
					{tab === "week" && weekProgram && today ? (
						<WeekTab
							content={content}
							program={weekProgram}
							places={places}
							today={today}
							todayIdx={todayIdx}
							day={day}
							activeEvent={openEvent?.id ?? null}
							hover={hover}
							onOpenEvent={(id) => open({ kind: "event", id })}
							onHover={setHover}
							onTrack={track}
						/>
					) : tab === "offers" && offers ? (
						<OffersTab
							content={content}
							contact={contact}
							onOpenPlace={(slug) => open({ kind: "place", slug })}
							onContact={(channel) => track("contact", channel)}
						/>
					) : tab === "contact" && contact ? (
						<ContactTab
							content={content}
							contact={contact}
							onOpenPlace={(slug) => open({ kind: "place", slug })}
							onContact={(channel) => track("contact", channel)}
						/>
					) : (
						<HighlightsTab
							content={content}
							places={places}
							tonight={tonight}
							active={active}
							hover={hover}
							onOpenBase={() => open({ kind: "base" })}
							onOpenPlace={(slug) => open({ kind: "place", slug })}
							onHover={setHover}
							onWeek={() => changeTab("week")}
							onOffers={() => changeTab("offers")}
							onContactTab={() => changeTab("contact")}
							onContact={(channel) => track("contact", channel)}
						/>
					)}
				</div>
				<div className={styles.railFoot}>
					{contact ? (
						// a restaurant or bar: its table first, and Stippl's save beside it, as two buttons on one line
						<>
							{offers &&
								(tab === "highlights" || tab === "offers") &&
								moreChip(tab === "highlights" ? t.highlightsBelow(content.highlights.length) : t.waysToEat(offers.cards.length + (sister ? 1 : 0)))}
							<a id="partnermap-book" className={styles.cta} href={contact.book} target="_blank" rel="noopener" onClick={() => track("contact", "book")}>
								<MarkerIcon name="p_restaurant" size={18} />
								{t.bookTable}
							</a>
							<a
								id="partnermap-save"
								className={cx(styles.cta, styles.ghost)}
								href={saveMapUrl(content)}
								target="_blank"
								rel="noopener"
								aria-label={t.saveMapLabel}
								onClick={() => track("save_map")}
							>
								<UiIcon name="save" />
								{t.saveMap}
							</a>
						</>
					) : (
						<>
							{lightHostel && tab === "highlights" && moreChip(t.highlightsBelow(content.highlights.length))}
							<a id="partnermap-save" className={styles.cta} href={saveMapUrl(content)} target="_blank" rel="noopener" onClick={() => track("save_map")}>
								<UiIcon name="save" />
								{t.saveThisMap}
							</a>
							<p>{weekProgram ? t.freeInStipplWeek : t.freeInStippl}</p>
						</>
					)}
				</div>
			</aside>

			<main className={styles.map} aria-label={header.mapLabel}>
				<LiveMap
					content={content}
					places={mapPlaces}
					active={active}
					hover={hover}
					walk={walk}
					camera={camera}
					panelRef={panelRef}
					onSelect={openFromMap}
					onHover={setHover}
					onHome={home}
				/>
				<div className={styles.legend} aria-hidden="true">
					<span className={styles.hk}>
						<i>
							<img src={partner.logo} alt="" />
						</i>
						{partner.shortName}
					</span>
					{(tab === "week" || content.highlights.length > 0) && (
						<span>
							<i style={{ background: placeFill(theme, tab !== "week" ? "highlight" : "venue") }}>
								<MarkerIcon name={tab !== "week" ? "star" : "ticket"} size={14} />
							</i>
							{tab !== "week" ? t.legendHighlights : day === todayIdx ? t.onToday : t.onDay(t.days[day])}
						</span>
					)}
				</div>
				{/* the href is the computer's, the same on server and client; a phone is sent to the app when it taps (device read at click time, not while rendering) */}
				<a
					id="partnermap-stippl"
					className={styles.maplogo}
					href={logoUrl(content)}
					target="_blank"
					rel="noopener"
					aria-label="Stippl"
					onClick={(e) => {
						if (isIOS || isAndroid) e.currentTarget.href = logoPhoneUrl(content);
					}}
				>
					<img src={StipplLogo} alt="" />
				</a>
				<Sheet
					content={content}
					places={places}
					today={today}
					day={day}
					todayIdx={todayIdx}
					selection={selected}
					panelRef={panelRef}
					onClose={close}
					onOffer={goToOffer}
					onTrack={track}
				/>
			</main>
		</div>
	);
};

export default PartnerMap;
